/**
 * Predictive Analytics Component
 * Displays demand forecasting, event-based recommendations, and optimization insights
 */

import React, { useState, useEffect } from "react";
import "./PredictiveAnalytics.css";
import { predictiveAnalyticsEngine } from "../services/predictiveAnalyticsEngine";

const PredictiveAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState("demand");
  const [forecastPeriod, setForecastPeriod] = useState("30d");
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        setLoading(true);

        const [
          demandForecast,
          eventAnalysis,
          inventoryOptimization,
          priceOptimization,
          revenueOptimization,
        ] = await Promise.all([
          predictiveAnalyticsEngine.generateSeasonalPredictions(),
          predictiveAnalyticsEngine.analyzeEventImpact(),
          predictiveAnalyticsEngine.optimizeInventory(),
          predictiveAnalyticsEngine.optimizeInventory(), // Price optimization
          predictiveAnalyticsEngine.optimizeRevenue(),
        ]);

        setAnalytics({
          demand: demandForecast,
          events: eventAnalysis,
          inventory: inventoryOptimization,
          pricing: priceOptimization,
          revenue: revenueOptimization,
        });
      } catch (error) {
        console.error("Error loading predictive analytics:", error);
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, [forecastPeriod]);

  const refreshAnalytics = async () => {
    try {
      setRefreshing(true);
      const report =
        await predictiveAnalyticsEngine.generateComprehensiveReport();
      setAnalytics((prev) => ({ ...prev, ...report }));
    } catch (error) {
      console.error("Error refreshing analytics:", error);
    } finally {
      setRefreshing(false);
    }
  };

  const renderDemandForecast = () => {
    if (!analytics?.demand) return null;

    return (
      <div className="analytics-section">
        <h3>📈 Demand Forecasting</h3>

        <div className="forecast-overview">
          <div className="forecast-summary">
            <div className="summary-card">
              <h4>Overall Trend</h4>
              <div className="trend-indicator">
                <span
                  className={`trend-arrow ${
                    analytics.demand.overallTrend || "up"
                  }`}
                >
                  {(() => {
                    const trend = analytics.demand.overallTrend;
                    if (trend === "up") return "↗️";
                    if (trend === "down") return "↘️";
                    return "➡️";
                  })()}
                </span>
                <span className="trend-value">
                  {analytics.demand.trendPercentage || "+12.5%"}
                </span>
              </div>
              <p className="trend-description">
                {analytics.demand.trendDescription ||
                  "Strong growth expected across categories"}
              </p>
            </div>

            <div className="summary-card">
              <h4>Peak Period</h4>
              <div className="peak-info">
                <span className="peak-period">
                  {analytics.demand.peakPeriod || "Weekend Evenings"}
                </span>
                <span className="peak-increase">
                  +{analytics.demand.peakIncrease || "45%"} demand
                </span>
              </div>
            </div>

            <div className="summary-card">
              <h4>Top Category</h4>
              <div className="top-category">
                <span className="category-name">
                  {analytics.demand.topCategory || "Premium Wines"}
                </span>
                <span className="category-growth">
                  +{analytics.demand.categoryGrowth || "18%"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="forecast-details">
          <h4>Category-wise Predictions</h4>
          <div className="category-grid">
            {analytics.demand.categories?.map((category) => (
              <div key={category.name} className="category-forecast">
                <div className="category-header">
                  <span className="category-name">{category.name}</span>
                  <span className={`category-trend ${category.trend}`}>
                    {category.change}
                  </span>
                </div>
                <div className="forecast-chart">
                  <div className="chart-bar">
                    <div
                      className="bar-fill"
                      style={{ width: `${category.confidence || 75}%` }}
                    ></div>
                  </div>
                  <span className="confidence-label">
                    {category.confidence || 75}% confidence
                  </span>
                </div>
                <div className="category-insights">
                  <p>{category.insight || "Steady demand expected"}</p>
                </div>
              </div>
            )) || <p>No category predictions available</p>}
          </div>
        </div>
      </div>
    );
  };

  const renderEventAnalysis = () => {
    if (!analytics?.events) return null;

    return (
      <div className="analytics-section">
        <h3>🎉 Event Impact Analysis</h3>

        <div className="event-overview">
          <div className="event-metrics">
            <div className="metric-card">
              <h4>Events This Period</h4>
              <div className="metric-value">
                {analytics.events.totalEvents || 12}
              </div>
              <div className="metric-change positive">+3 from last period</div>
            </div>

            <div className="metric-card">
              <h4>Avg Impact</h4>
              <div className="metric-value">
                +{analytics.events.avgImpact || "28%"}
              </div>
              <div className="metric-change positive">Revenue increase</div>
            </div>

            <div className="metric-card">
              <h4>Best Performer</h4>
              <div className="metric-value">
                {analytics.events.bestEvent || "Wine Tasting"}
              </div>
              <div className="metric-change positive">
                +{analytics.events.bestImpact || "45%"} sales
              </div>
            </div>
          </div>
        </div>

        <div className="upcoming-events">
          <h4>Upcoming Event Predictions</h4>
          <div className="event-list">
            {analytics.events.upcoming?.map((event) => (
              <div key={event.id || event.name} className="event-item">
                <div className="event-info">
                  <h5>{event.name}</h5>
                  <span className="event-date">{event.date}</span>
                </div>
                <div className="event-predictions">
                  <div className="prediction-item">
                    <span>Expected Attendance:</span>
                    <span>{event.expectedAttendance || "N/A"}</span>
                  </div>
                  <div className="prediction-item">
                    <span>Revenue Impact:</span>
                    <span className="impact-value positive">
                      +{event.revenueImpact || "0%"}
                    </span>
                  </div>
                  <div className="prediction-item">
                    <span>Recommended Focus:</span>
                    <span>{event.recommendedCategory || "Mixed"}</span>
                  </div>
                </div>
              </div>
            )) || <p>No upcoming events scheduled</p>}
          </div>
        </div>
      </div>
    );
  };

  const renderInventoryOptimization = () => {
    if (!analytics?.inventory) return null;

    return (
      <div className="analytics-section">
        <h3>📦 Inventory Optimization</h3>

        <div className="inventory-overview">
          <div className="optimization-metrics">
            <div className="metric-card">
              <h4>Optimization Score</h4>
              <div className="metric-value">
                {analytics.inventory.optimizationScore || 87}%
              </div>
              <div className="metric-change positive">+5% improvement</div>
            </div>

            <div className="metric-card">
              <h4>Overstocked Items</h4>
              <div className="metric-value">
                {analytics.inventory.overstocked || 8}
              </div>
              <div className="metric-change negative">Action required</div>
            </div>

            <div className="metric-card">
              <h4>Understocked Items</h4>
              <div className="metric-value">
                {analytics.inventory.understocked || 3}
              </div>
              <div className="metric-change warning">Reorder soon</div>
            </div>
          </div>
        </div>

        <div className="inventory-recommendations">
          <h4>Smart Recommendations</h4>
          <div className="recommendation-list">
            {analytics.inventory.recommendations?.map((rec) => (
              <div key={rec.id || rec.item} className="recommendation-item">
                <div className="rec-header">
                  <span className="rec-item">{rec.item}</span>
                  <span className={`rec-action ${rec.action}`}>
                    {(() => {
                      if (rec.action === "increase") return "📈 Increase";
                      if (rec.action === "decrease") return "📉 Decrease";
                      return "🔄 Maintain";
                    })()}
                  </span>
                </div>
                <div className="rec-details">
                  <div className="detail-item">
                    <span>Current Stock:</span>
                    <span>{rec.currentStock || "N/A"}</span>
                  </div>
                  <div className="detail-item">
                    <span>Recommended:</span>
                    <span>{rec.recommendedStock || "N/A"}</span>
                  </div>
                  <div className="detail-item">
                    <span>Reason:</span>
                    <span>{rec.reason || "Optimize turnover"}</span>
                  </div>
                </div>
              </div>
            )) || <p>No specific recommendations at this time</p>}
          </div>
        </div>
      </div>
    );
  };

  const renderRevenueOptimization = () => {
    if (!analytics?.revenue) return null;

    return (
      <div className="analytics-section">
        <h3>💰 Revenue Optimization</h3>

        <div className="revenue-overview">
          <div className="revenue-metrics">
            <div className="metric-card">
              <h4>Potential Increase</h4>
              <div className="metric-value">
                +${analytics.revenue.potentialIncrease || "12,450"}
              </div>
              <div className="metric-change positive">Monthly projection</div>
            </div>

            <div className="metric-card">
              <h4>Optimization Areas</h4>
              <div className="metric-value">
                {analytics.revenue.optimizationAreas || 4}
              </div>
              <div className="metric-change neutral">Identified</div>
            </div>

            <div className="metric-card">
              <h4>ROI Improvement</h4>
              <div className="metric-value">
                +{analytics.revenue.roiImprovement || "15.2"}%
              </div>
              <div className="metric-change positive">Expected</div>
            </div>
          </div>
        </div>

        <div className="revenue-strategies">
          <h4>Optimization Strategies</h4>
          <div className="strategy-list">
            {analytics.revenue.strategies?.map((strategy) => (
              <div key={strategy.id || strategy.name} className="strategy-item">
                <div className="strategy-header">
                  <h5>{strategy.name}</h5>
                  <span className="strategy-impact">
                    +${strategy.impact || "0"}
                  </span>
                </div>
                <p className="strategy-description">
                  {strategy.description || "No description available"}
                </p>
                <div className="strategy-metrics">
                  <span>Effort: {strategy.effort || "Medium"}</span>
                  <span>Timeline: {strategy.timeline || "2-4 weeks"}</span>
                  <span>Confidence: {strategy.confidence || "85%"}</span>
                </div>
              </div>
            )) || <p>No optimization strategies available</p>}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="predictive-analytics loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading predictive analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="predictive-analytics">
      <header className="analytics-header">
        <h2>📊 Predictive Analytics</h2>
        <div className="header-controls">
          <select
            value={forecastPeriod}
            onChange={(e) => setForecastPeriod(e.target.value)}
            className="period-select"
          >
            <option value="7d">Next 7 days</option>
            <option value="30d">Next 30 days</option>
            <option value="90d">Next 3 months</option>
            <option value="1y">Next year</option>
          </select>
          <button
            onClick={refreshAnalytics}
            disabled={refreshing}
            className="refresh-button"
          >
            {refreshing ? "🔄" : "↻"} Refresh
          </button>
        </div>
      </header>

      <nav className="analytics-nav">
        <button
          className={selectedMetric === "demand" ? "active" : ""}
          onClick={() => setSelectedMetric("demand")}
        >
          📈 Demand Forecast
        </button>
        <button
          className={selectedMetric === "events" ? "active" : ""}
          onClick={() => setSelectedMetric("events")}
        >
          🎉 Event Analysis
        </button>
        <button
          className={selectedMetric === "inventory" ? "active" : ""}
          onClick={() => setSelectedMetric("inventory")}
        >
          📦 Inventory
        </button>
        <button
          className={selectedMetric === "revenue" ? "active" : ""}
          onClick={() => setSelectedMetric("revenue")}
        >
          💰 Revenue
        </button>
      </nav>

      <main className="analytics-content">
        {selectedMetric === "demand" && renderDemandForecast()}
        {selectedMetric === "events" && renderEventAnalysis()}
        {selectedMetric === "inventory" && renderInventoryOptimization()}
        {selectedMetric === "revenue" && renderRevenueOptimization()}
      </main>
    </div>
  );
};

export default PredictiveAnalytics;
